import { useState } from 'react';
import { IServiceRequestCSVResponseData } from 'src/services/client-types';
import { fetchSelectedServiceRequestCSVExportDownloadList } from 'src/services/client';
import { toast } from 'react-toastify';
import {
  GeneratedCSVInfo,
  ITransformedServiceRequest,
  ITransformedServiceRequestCSVResponse,
} from 'src/pages/SharedPageCustomHooks/UseDownloadCSV/types';
import {
  downloadCSV,
  generateCSVFromAPIResponse,
  transformServiceRequestsForCSV,
} from 'src/pages/SharedPageCustomHooks/UseDownloadCSV/helpers';
import { serviceRequestCsvHeadersConfigObj } from 'src/pages/SharedPageCustomHooks/UseDownloadCSV/serviceRequestCsvHeaderConfig';

export const useDownloadCSVData = () => {
  const [responseData, setResponseData] =
    useState<ITransformedServiceRequestCSVResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [shouldDownload, setShouldDownload] = useState<boolean>(false); // Add this!

  const onDownloadCSV = async (
    serviceRequestIds: string[]
  ): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    setResponseData(null);
    setShouldDownload(false);

    try {
      const response: any =
        await fetchSelectedServiceRequestCSVExportDownloadList(
          serviceRequestIds
        );

      if (!response.ok) {
        throw new Error(`CSV download failed with status ${response.status}`);
      }

      const csvExportDownloadLisrApiResponse: IServiceRequestCSVResponseData =
        await response.json();

      if (
        !csvExportDownloadLisrApiResponse.serviceRequestList ||
        csvExportDownloadLisrApiResponse.serviceRequestList.length === 0
      ) {
        throw new Error('No service requests found in response');
      }

      const transformedServiceRequestsForCSV: ITransformedServiceRequest[] =
        transformServiceRequestsForCSV(
          csvExportDownloadLisrApiResponse.serviceRequestList
        );

      const transformedResponse: ITransformedServiceRequestCSVResponse = {
        ...csvExportDownloadLisrApiResponse,
        serviceRequestList: transformedServiceRequestsForCSV,
      };

      const { csv, filename }: GeneratedCSVInfo = generateCSVFromAPIResponse(
        transformedResponse,
        serviceRequestCsvHeadersConfigObj
      );

      downloadCSV(csv, filename);

      setResponseData(transformedResponse);
      setShouldDownload(true);

      toast.success('✅ CSV data prepared successfully!', {
        autoClose: 5000,
        position: 'top-center',
      });

      return true;
    } catch (err: unknown) {
      console.error('CSV download failed:', err);
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'CSV download failed, please try again.';
      setError(errorMessage);
      toast.error(`🚫 ${errorMessage}`, {
        autoClose: 12000,
        position: 'top-center',
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const clearData = (): void => {
    setResponseData(null);
    setError(null);
    setShouldDownload(false);
  };

  return {
    responseData,
    isLoading,
    error,
    shouldDownload,
    onDownloadCSV,
    clearData,
  };
};
